/**
 * NOTE: This code belongs to Kangaroo Rewards. Unauthorized use is prohibited
 */

@file:Suppress("unused", "MemberVisibilityCanBePrivate", "EXPERIMENTAL_API_USAGE")
@file:OptIn(ExperimentalJsExport::class)
package features.userBusinessSocialMediaLinks

import kangaroorewards.appsdk.core.api.Api
import kangaroorewards.appsdk.core.domain.Result
import kangaroorewards.appsdk.core.domain.Result.Idle
import kangaroorewards.appsdk.core.domain.Result.Loading
import kangaroorewards.appsdk.core.domain.SerializedResult
import kangaroorewards.appsdk.core.domain.toJsonResult
import kangaroorewards.appsdk.core.utils.CFlow
import kangaroorewards.appsdk.core.utils.asCFlow
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.mapLatest
import kotlinx.serialization.InternalSerializationApi
import kotlin.js.ExperimentalJsExport
import features.userBusinessSocialMediaLinks.internal.UserBusinessSocialMediaLinksUseCase
import features.userBusinessSocialMediaLinks.internal.UserBusinessSocialMediaLinksUseCase.Params
import features.userBusinessSocialMediaLinks.models.SocialMediaLinksModel


typealias UserBusinessSocialMediaLinksState = CFlow<UserBusinessSocialMediaLinksUseCaseResult?>

typealias UserBusinessSocialMediaLinksUseCaseResult = Result<SocialMediaLinksModel>

fun UserBusinessSocialMediaLinksUseCaseResult.serializeUserBusinessSocialMediaLinksApiResult(): SerializedResult<String> {
    return this.toJsonResult<SocialMediaLinksModel>()
}

/**
 * Serializes [UserBusinessSocialMediaLinksState]'s data and returns a new
 * [CFlow] containing data serialized as a JSON [String].
 */
@OptIn(ExperimentalCoroutinesApi::class)
fun UserBusinessSocialMediaLinksState.serializeUserBusinessSocialMediaLinksState(): CFlow<SerializedResult<String>?> {
    return this.mapLatest {
        it?.toJsonResult<SocialMediaLinksModel>()
    }.asCFlow()
}

/**
 * Get a list of social media links for this business [Api] class
 */
class UserBusinessSocialMediaLinksApi : Api() {

    val userBusinessSocialMediaLinksState: UserBusinessSocialMediaLinksState
        get() = _userBusinessSocialMediaLinksState.asCFlow()

    companion object {
        private val _userBusinessSocialMediaLinksState: MutableStateFlow<UserBusinessSocialMediaLinksUseCaseResult> = MutableStateFlow(Idle())
    }

    private val userBusinessSocialMediaLinksUseCase = UserBusinessSocialMediaLinksUseCase()

    /**
     * Get a list of social media links for this business
     */
    suspend fun getUserBusinessSocialMediaLinks(
        overrideHeaders: Map<String, String>? = null,
        businessId: String
    ): Result<SocialMediaLinksModel> {
        /* set state to Loading as soon as function is called */
        _userBusinessSocialMediaLinksState.value = Loading()
        val result = this.userBusinessSocialMediaLinksUseCase(
            Params(
                overrideHeaders = overrideHeaders,
                businessId = businessId
            )
        ) { result ->
            _userBusinessSocialMediaLinksState.value = result
            return@userBusinessSocialMediaLinksUseCase result
        }
        return result
    }

}
